import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { QRCodeSVG } from "qrcode.react";
import { StreamingText } from "@/components/StreamingText";
import { 
  Clock, 
  BarChart3, 
  Sparkles,
  ChevronRight,
  BookOpen,
  Target,
  Award,
  LogIn,
  LogOut,
  User,
  Calendar,
  Share2,
  Play,
  CheckCircle2,
  XCircle,
  Trophy,
  Lightbulb,
  Lock,
  Shield,
  Infinity,
  Loader2,
  X
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// Colorful brain images
const BRAIN_LOGO = "https://customer-assets.emergentagent.com/job_e95fcea1-1d05-419a-a729-ac97ff58a502/artifacts/1u3r91fj_logo%20no%20back_Image_Jan_4__2026_at_02_55_25_PM-removebg-preview.png";
const BRAIN_HERO = "https://static.prod-images.emergentagent.com/jobs/8e9d7372-4ec9-4003-8f1e-d03dc24a1b4c/images/93601eff91917688447307082924b3850f21d5246b5d355253c68a963eb104a3.png";
const BRAIN_QUIZ = "https://static.prod-images.emergentagent.com/jobs/8e9d7372-4ec9-4003-8f1e-d03dc24a1b4c/images/c8189de064c0a987b2b301aff93af08dae03f473f98554da8e4c62d9a5cb5213.png";

export default function LandingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, isPremium, isAdmin, logout } = useAuth();
  const [adminVerified, setAdminVerified] = useState(false);
  
  // Free quiz state
  const [showFreeQuiz, setShowFreeQuiz] = useState(false);
  const [freeQuizData, setFreeQuizData] = useState(null);
  const [freeQuizLoading, setFreeQuizLoading] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [userAnswers, setUserAnswers] = useState([]);
  const [quizComplete, setQuizComplete] = useState(false);
  
  // Sample Study Plan state
  const [showSampleStudyPlan, setShowSampleStudyPlan] = useState(false);
  
  // Quiz mode state
  const [quizStarted, setQuizStarted] = useState(false);
  const [isStudyMode, setIsStudyMode] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const FREE_QUIZ_TIME_LIMIT = 10; // 10 minutes for 10 questions
  
  // AI Explanation state
  const [aiExplanation, setAiExplanation] = useState(null);
  const [loadingExplanation, setLoadingExplanation] = useState(false);
  
  // AbortController ref to cancel pending AI explanation requests
  const abortControllerRef = useRef(null);
  // Track which question the current explanation request is for
  const currentExplanationQuestionRef = useRef(null);

  // Cancel any pending AI explanation request
  const cancelPendingExplanation = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    currentExplanationQuestionRef.current = null;
    setLoadingExplanation(false);
    setAiExplanation(null);
  }, []);

  // Cleanup AbortController on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Clear explanation state when question changes - safety net
  useEffect(() => {
    // Abort any pending request when question index changes
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    currentExplanationQuestionRef.current = null;
    setAiExplanation(null);
    setLoadingExplanation(false);
  }, [currentQuestion]);

  // Auto-open free quiz if URL has ?quiz=free parameter
  useEffect(() => {
    if (searchParams.get('quiz') === 'free' && !showFreeQuiz && !freeQuizData) {
      startFreeQuiz();
    }
  }, [searchParams]);

  // Timer effect for free quiz
  useEffect(() => {
    if (!quizStarted || quizComplete || !showFreeQuiz) return;

    // Study mode: count up (no time limit)
    if (isStudyMode) {
      const timer = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
      return () => clearInterval(timer);
    }

    // Timed mode: count down
    if (timeRemaining <= 0) {
      // Time's up - auto complete the quiz
      completeQuizDueToTime();
      return;
    }

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [quizStarted, quizComplete, isStudyMode, timeRemaining, showFreeQuiz]);

  // Auto-complete quiz when time runs out
  const completeQuizDueToTime = () => {
    if (!freeQuizData || quizComplete) return;
    
    // Fill remaining questions as unanswered
    const finalAnswers = [...userAnswers];
    for (let i = userAnswers.length; i < freeQuizData.questions.length; i++) {
      finalAnswers.push({
        question: freeQuizData.questions[i].text,
        selected: null,
        correct: freeQuizData.questions[i].correct_answer,
        isCorrect: false,
        explanation: freeQuizData.questions[i].explanation,
        options: freeQuizData.questions[i].options
      });
    }
    setUserAnswers(finalAnswers);
    setQuizComplete(true);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimerColor = () => {
    const totalTime = FREE_QUIZ_TIME_LIMIT * 60;
    const percentRemaining = (timeRemaining / totalTime) * 100;
    if (percentRemaining <= 10) return "text-red-500";
    if (percentRemaining <= 25) return "text-amber-500";
    return "text-slate-700 dark:text-slate-300";
  };

  // Verify admin status with API as backup
  useEffect(() => {
    const verifyAdmin = async () => {
      if (isAdmin) {
        setAdminVerified(true);
        return;
      }
      const token = localStorage.getItem("token");
      if (!token) return;
      try {
        const response = await axios.get(`${API}/admin/check`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.is_admin) {
          setAdminVerified(true);
        }
      } catch (error) {
        // Not admin or error
      }
    };
    if (isAuthenticated) {
      verifyAdmin();
    }
  }, [isAuthenticated, isAdmin]);

  const showAdminButton = isAdmin || adminVerified;

  const handleLogout = async () => {
    await logout();
  };

  // Start free quiz (just loads data, doesn't start timer)
  const startFreeQuiz = async () => {
    setFreeQuizLoading(true);
    try {
      const response = await axios.get(`${API}/free-quiz`);
      setFreeQuizData(response.data);
      setShowFreeQuiz(true);
      setQuizStarted(false);
      setCurrentQuestion(0);
      setSelectedAnswer(null);
      setUserAnswers([]);
      setQuizComplete(false);
      setIsStudyMode(false);
      setTimeRemaining(FREE_QUIZ_TIME_LIMIT * 60);
      setElapsedTime(0);
    } catch (error) {
      console.error("Failed to load free quiz:", error);
    } finally {
      setFreeQuizLoading(false);
    }
  };

  // Start quiz with selected mode
  const beginQuiz = (studyMode) => {
    setIsStudyMode(studyMode);
    setQuizStarted(true);
    if (!studyMode) {
      setTimeRemaining(FREE_QUIZ_TIME_LIMIT * 60);
    }
    setElapsedTime(0);
    setAiExplanation(null);
    setLoadingExplanation(false);
  };

  // Handle answer selection with AI explanation
  const handleSelectAnswer = async (answer) => {
    if (selectedAnswer !== null) return; // Already answered
    setSelectedAnswer(answer);
    
    const currentQ = freeQuizData.questions[currentQuestion];
    const isCorrect = answer === currentQ.correct_answer;
    const questionIndex = currentQuestion; // Capture question index for this request
    
    // Cancel any previous pending request first
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Track which question this explanation is for
    currentExplanationQuestionRef.current = questionIndex;
    
    // Start loading AI explanation
    setLoadingExplanation(true);
    setAiExplanation("");
    
    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    try {
      const response = await fetch(`${API}/explain-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          question: currentQ.text,
          selected_answer: answer,
          correct_answer: currentQ.correct_answer,
          is_correct: isCorrect,
          all_options: currentQ.options
        }),
        signal: abortController.signal
      });
      
      // Check if aborted before processing response
      if (abortController.signal.aborted || currentExplanationQuestionRef.current !== questionIndex) {
        return;
      }
      
      if (!response.ok) {
        throw new Error('Failed to fetch explanation');
      }
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let streamedText = "";
      
      try {
        // Stream the response
        while (true) {
          // Check if aborted or question changed before each read
          if (abortController.signal.aborted || currentExplanationQuestionRef.current !== questionIndex) {
            await reader.cancel();
            return;
          }
          
          const { done, value } = await reader.read();
          if (done) break;
          
          // Double-check after async read
          if (abortController.signal.aborted || currentExplanationQuestionRef.current !== questionIndex) {
            await reader.cancel();
            return;
          }
          
          const chunk = decoder.decode(value, { stream: true });
          streamedText += chunk;
          
          // Only update if this is still the current question
          if (currentExplanationQuestionRef.current === questionIndex && !abortController.signal.aborted) {
            setAiExplanation(streamedText);
          }
        }
      } finally {
        try { await reader.cancel(); } catch (e) { /* ignore */ }
      }
      
      // Only update loading state if still on same question
      if (currentExplanationQuestionRef.current === questionIndex && !abortController.signal.aborted) {
        setLoadingExplanation(false);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      console.error("Failed to get AI explanation:", error);
      if (currentExplanationQuestionRef.current === questionIndex) {
        setAiExplanation(null);
        setLoadingExplanation(false);
      }
    }
  };

  // Move to next question or complete quiz
  const handleNextQuestion = () => {
    // Cancel any pending AI explanation request
    cancelPendingExplanation();
    
    const currentQ = freeQuizData.questions[currentQuestion];
    const isCorrect = selectedAnswer === currentQ.correct_answer;
    
    setUserAnswers([...userAnswers, {
      question: currentQ.text,
      selected: selectedAnswer,
      correct: currentQ.correct_answer,
      isCorrect,
      explanation: currentQ.explanation,
      aiExplanation: aiExplanation,
      source: currentQ.source,
      options: currentQ.options
    }]);

    if (currentQuestion < freeQuizData.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setAiExplanation(null);
      setLoadingExplanation(false);
    } else {
      setQuizComplete(true);
    }
  };

  // Close free quiz
  const closeFreeQuiz = () => {
    cancelPendingExplanation();
    setShowFreeQuiz(false);
    setFreeQuizData(null);
    setQuizStarted(false);
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setUserAnswers([]);
    setQuizComplete(false);
    setIsStudyMode(false);
    setTimeRemaining(0);
    setElapsedTime(0);
    setAiExplanation(null);
    setLoadingExplanation(false);
  };

  // Calculate score
  const getScore = () => {
    return userAnswers.filter(a => a.isCorrect).length;
  };

  // Smart navigation based on auth and premium status
  const handleStartPractice = () => {
    if (isAuthenticated && isPremium) {
      // Premium user - go directly to quizzes
      navigate("/quizzes");
    } else {
      // Not logged in or not premium - go to auth page
      navigate("/auth");
    }
  };

  const features = [
    {
      icon: null,
      useBrainImage: true,
      title: "BCBA Exam Topics",
      description: "Comprehensive coverage of all BCBA task list areas including measurement, behavior change, and ethics."
    },
    {
      icon: Clock,
      title: "Timed Practice",
      description: "Simulate real exam conditions with timed quizzes to build speed and confidence."
    },
    {
      icon: Sparkles,
      title: "AI Explanations",
      description: "Get detailed AI-powered explanations for every answer to deepen understanding."
    },
    {
      icon: BarChart3,
      title: "Track Progress",
      description: "Monitor your performance with analytics and identify areas for improvement."
    }
  ];

  const categories = [
    { name: "Measurement", color: "bg-sky-500", questions: 15 },
    { name: "Behavior Change", color: "bg-teal-500", questions: 20 },
    { name: "Ethics", color: "bg-amber-500", questions: 12 },
    { name: "Assessment", color: "bg-rose-400", questions: 15 },
    { name: "Mock Exams", color: "bg-cyan-600", questions: 450 }
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 dark:bg-slate-900/90 backdrop-blur-lg border-b border-slate-200 dark:border-slate-700">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl overflow-hidden">
              <img src={BRAIN_LOGO} alt="Brain Logo" className="w-full h-full object-cover" />
            </div>
            <span className="font-bold text-xl tracking-tight text-slate-800 dark:text-slate-100" data-testid="app-logo">ABA Prep Lab</span>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              onClick={() => navigate("/analytics")}
              className="text-slate-600 hover:text-teal-600 dark:text-slate-300"
              data-testid="nav-analytics-btn"
            >
              Analytics
            </Button>
            {isAuthenticated ? (
              <>
                {showAdminButton && (
                  <Button 
                    variant="outline"
                    onClick={() => navigate("/admin")}
                    className="border-violet-300 text-violet-700 hover:bg-violet-50"
                    data-testid="nav-admin-btn"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Admin
                  </Button>
                )}
                <Button 
                  onClick={() => navigate("/quizzes")}
                  className="bg-teal-500 hover:bg-teal-600 text-white"
                  data-testid="nav-start-btn"
                >
                  Start Practice
                </Button>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-600 hidden sm:inline">
                    Hi, {user?.name?.split(' ')[0]}
                  </span>
                  <Button 
                    variant="outline"
                    onClick={handleLogout}
                    className="border-slate-300 text-slate-700"
                    data-testid="nav-logout-btn"
                  >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </div>
              </>
            ) : (
              <Button 
                onClick={() => navigate("/auth")}
                className="bg-teal-500 hover:bg-teal-600 text-white"
                data-testid="nav-create-account-btn"
              >
                Log In or Create Account
              </Button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-300 text-sm font-semibold mb-6">
                <Award className="w-4 h-4" />
                BCBA Exam Preparation
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold tracking-tight mb-6 leading-tight text-slate-900 dark:text-slate-100" data-testid="hero-title">
                Master ABA with
                <span className="text-teal-600 dark:text-teal-400 block">Interactive Quizzes</span>
              </h1>
              <p className="text-lg text-slate-700 dark:text-slate-300 mb-8 leading-relaxed max-w-lg font-medium">
                Prepare for your BCBA certification with AI-powered practice quizzes, 
                detailed explanations, and performance tracking.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg"
                  onClick={handleStartPractice}
                  className="bg-teal-500 hover:bg-teal-600 text-white text-lg px-8 py-6 rounded-xl shadow-lg shadow-teal-500/20 hover:scale-105 transition-transform"
                  data-testid="hero-start-btn"
                >
                  {isAuthenticated && isPremium ? "Start Practicing" : "Log In or Create Account"}
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={startFreeQuiz}
                  disabled={freeQuizLoading}
                  className="text-lg px-8 py-6 rounded-xl hover:scale-105 transition-transform border-violet-300 text-violet-600 hover:bg-violet-50 dark:border-violet-600 dark:text-violet-400"
                  data-testid="hero-free-quiz-btn"
                >
                  <Play className="w-5 h-5 mr-2" />
                  {freeQuizLoading ? "Loading..." : "Try Free Quiz"}
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative flex items-center justify-center">
                <motion.img 
                  src={BRAIN_HERO}
                  alt="Colorful Brain"
                  className="w-full max-w-md h-auto drop-shadow-2xl rounded-3xl"
                  data-testid="hero-image"
                  animate={{ 
                    y: [0, -10, 0],
                    scale: [1, 1.02, 1]
                  }}
                  transition={{ 
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
              </div>
              
              {/* Floating stats card */}
              <motion.div 
                className="absolute -bottom-6 -left-6 bg-white dark:bg-slate-800 rounded-2xl p-5 shadow-xl border border-slate-100 dark:border-slate-700"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                    <Target className="w-6 h-6 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">530+</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Practice Questions</p>
                  </div>
                </div>
              </motion.div>

              {/* Floating badge */}
              <motion.div 
                className="absolute -top-4 -right-4 bg-teal-500 text-white rounded-full px-4 py-2 font-semibold text-sm shadow-lg"
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.7, type: "spring" }}
              >
                AI-Powered
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-white dark:bg-slate-800/50">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-4 text-slate-900 dark:text-slate-100" data-testid="features-title">
              Everything You Need to Pass
            </h2>
            <p className="text-slate-700 dark:text-slate-300 max-w-2xl mx-auto font-medium">
              Our comprehensive quiz platform covers all BCBA task list areas with 
              intelligent features to maximize your study efficiency.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="stat-card p-6 rounded-2xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center mb-4 overflow-hidden">
                  {feature.useBrainImage ? (
                    <img src={BRAIN_QUIZ} alt="Brain" className="w-10 h-10 object-cover" />
                  ) : (
                    <feature.icon className="w-6 h-6 text-teal-600 dark:text-teal-400" />
                  )}
                </div>
                <h3 className="font-bold text-lg mb-2 text-slate-900 dark:text-slate-100">{feature.title}</h3>
                <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Study Plan CTA */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            className="bg-gradient-to-r from-teal-500 to-emerald-600 rounded-2xl p-8 text-white"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
                  <Calendar className="w-7 h-7" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-1">Personalized Study Plan</h3>
                  <p className="text-white/80 text-sm">Get a customized study schedule based on your weak areas</p>
                </div>
              </div>
              <Button 
                onClick={() => setShowSampleStudyPlan(true)}
                className="bg-white text-teal-600 hover:bg-white/90 font-semibold px-6 py-5"
                data-testid="study-plan-btn"
              >
                <Calendar className="w-4 h-4 mr-2" />
                View Study Plan
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Premium Pricing Banner */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-3xl"
            style={{
              background: 'linear-gradient(135deg, #1e3a5f 0%, #0d9488 50%, #0f766e 100%)'
            }}
          >
            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-full h-full opacity-10">
              <div className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full blur-3xl" />
              <div className="absolute bottom-10 right-10 w-40 h-40 bg-violet-400 rounded-full blur-3xl" />
            </div>
            
            <div className="relative z-10 py-12 px-8 text-center">
              {/* Ribbon */}
              <div className="inline-block mb-6">
                <div className="bg-violet-600 text-white px-8 py-2 rounded-full font-bold text-lg shadow-lg shadow-violet-600/30">
                  ONLY
                </div>
              </div>
              
              {/* Price */}
              <div className="flex items-center justify-center gap-1 mb-4">
                <span className="text-amber-400 text-5xl md:text-6xl font-bold drop-shadow-lg">$</span>
                <span className="text-amber-400 text-7xl md:text-8xl font-bold drop-shadow-lg">7</span>
                <span className="text-white text-7xl md:text-8xl font-bold drop-shadow-lg">.00</span>
              </div>
              
              <p className="text-white/90 text-2xl md:text-3xl font-medium mb-6">
                per month.
              </p>
              
              {/* Tagline */}
              <p className="text-amber-300 text-xl md:text-2xl font-semibold italic mb-8">
                Affordable. Simple. No Commitment.
              </p>
              
              {/* Features list */}
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                {[
                  "530+ Practice Questions",
                  "AI-Powered Explanations", 
                  "4 Full Mock Exams",
                  "Cancel Anytime"
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                    <CheckCircle2 className="w-4 h-4 text-teal-300" />
                    <span className="text-white text-sm font-medium">{feature}</span>
                  </div>
                ))}
              </div>
              
              {/* CTA Button */}
              <Button
                size="lg"
                onClick={() => navigate(isAuthenticated ? "/subscription" : "/auth")}
                className="bg-white text-teal-700 hover:bg-white/90 font-bold text-lg px-10 py-6 rounded-xl shadow-xl hover:scale-105 transition-all"
                data-testid="premium-cta-btn"
              >
                {isPremium ? "You're Premium!" : "Access Premium Content"}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            className="hero-gradient rounded-3xl p-12 text-center text-white relative overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="relative z-10">
              <img src={BRAIN_QUIZ} alt="Brain" className="w-16 h-16 mx-auto mb-6 opacity-90" />
              <h2 className="text-3xl lg:text-4xl font-bold mb-4 text-white" data-testid="cta-title">
                Ready to Ace Your BCBA Exam?
              </h2>
              <p className="text-lg text-white/90 mb-8 max-w-lg mx-auto font-medium">
                Start practicing now and track your progress toward certification success.
              </p>
              <Button 
                size="lg"
                onClick={() => {
                  // Scroll to top and trigger free quiz
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  setTimeout(() => {
                    setShowFreeQuiz(true);
                  }, 300);
                }}
                className="bg-white text-teal-700 hover:bg-teal-50 text-lg px-8 py-6 rounded-xl shadow-lg hover:scale-105 transition-transform font-semibold"
                data-testid="cta-start-btn"
              >
                Begin Practice Session
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg overflow-hidden">
              <img src={BRAIN_LOGO} alt="Brain Logo" className="w-full h-full object-cover" />
            </div>
            <span className="font-bold text-slate-800 dark:text-slate-200">ABA Prep Lab</span>
          </div>
          
          {/* QR Code */}
          <div className="flex items-center gap-4">
            <div className="bg-white p-2 rounded-lg shadow-sm border border-slate-200">
              <QRCodeSVG 
                value={window.location.origin}
                size={64}
                level="M"
                data-testid="share-qr-code"
              />
            </div>
            <div className="text-left">
              <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1">
                <Share2 className="w-3 h-3" />
                Share App
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Scan to share</p>
            </div>
          </div>
          
          <div className="flex flex-col items-center md:items-end gap-2">
            <p className="text-sm font-medium text-slate-600 dark:text-slate-400" data-testid="footer-text">
              Helping behavior analysts succeed since 2024
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/privacy-policy")}
                className="text-xs text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 underline"
              >
                Privacy Policy
              </button>
              <span className="text-slate-400">|</span>
              <button
                onClick={() => navigate("/terms-of-service")}
                className="text-xs text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 underline"
              >
                Terms of Service
              </button>
            </div>
            <a 
              href="mailto:abapreplab@mail.com" 
              className="text-xs text-slate-600 hover:text-teal-600 dark:text-slate-400 dark:hover:text-teal-400 transition-colors"
              data-testid="contact-email"
            >
              Contact Us at abapreplab@mail.com
            </a>
          </div>
        </div>
      </footer>

      {/* Free Quiz Modal */}
      <AnimatePresence>
        {showFreeQuiz && freeQuizData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={(e) => e.target === e.currentTarget && !quizComplete && closeFreeQuiz()}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-slate-800 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl my-8"
              data-testid="free-quiz-modal"
            >
              {/* Mode Selection Screen */}
              {!quizStarted && !quizComplete ? (
                <div className="p-8">
                  <div className="text-center mb-8">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center mx-auto mb-4">
                      <Play className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">Free Sample Quiz</h2>
                    <p className="text-slate-600 dark:text-slate-400">
                      {freeQuizData.questions.length} questions • Choose your mode
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    {/* Timed Mode */}
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => beginQuiz(false)}
                      className="p-6 rounded-2xl border-2 border-violet-200 bg-violet-50 hover:border-violet-400 hover:bg-violet-100 transition-all text-left"
                    >
                      <div className="w-12 h-12 rounded-xl bg-violet-500 flex items-center justify-center mb-4">
                        <Clock className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-900 mb-1">Timed Mode</h3>
                      <p className="text-sm text-slate-600 mb-3">
                        {FREE_QUIZ_TIME_LIMIT} minutes to complete all questions
                      </p>
                      <div className="flex items-center gap-2 text-violet-600 text-sm font-medium">
                        <Clock className="w-4 h-4" />
                        Simulates exam conditions
                      </div>
                    </motion.button>

                    {/* Study Mode */}
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => beginQuiz(true)}
                      className="p-6 rounded-2xl border-2 border-teal-200 bg-teal-50 hover:border-teal-400 hover:bg-teal-100 transition-all text-left"
                    >
                      <div className="w-12 h-12 rounded-xl bg-teal-500 flex items-center justify-center mb-4">
                        <BookOpen className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-lg font-bold text-slate-900 mb-1">Study Mode</h3>
                      <p className="text-sm text-slate-600 mb-3">
                        No time limit, learn at your own pace
                      </p>
                      <div className="flex items-center gap-2 text-teal-600 text-sm font-medium">
                        <Sparkles className="w-4 h-4" />
                        Best for learning
                      </div>
                    </motion.button>
                  </div>

                  <Button
                    variant="ghost"
                    onClick={closeFreeQuiz}
                    className="w-full text-slate-500 hover:text-slate-700"
                  >
                    Cancel
                  </Button>
                </div>
              ) : !quizComplete ? (
                <>
                  {/* Quiz Header */}
                  <div className="bg-gradient-to-r from-violet-500 to-purple-600 p-6 sticky top-0 z-10">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                          {isStudyMode ? (
                            <BookOpen className="w-5 h-5 text-white" />
                          ) : (
                            <Clock className="w-5 h-5 text-white" />
                          )}
                        </div>
                        <div>
                          <h2 className="text-xl font-bold text-white">
                            {isStudyMode ? "Study Mode" : "Timed Quiz"}
                          </h2>
                          <p className="text-white/80 text-sm">
                            Question {currentQuestion + 1} of {freeQuizData.questions.length}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {/* Timer Display */}
                        <div className={`flex items-center gap-2 px-4 py-2 rounded-xl bg-white/20 ${!isStudyMode && getTimerColor()}`}>
                          <Clock className="w-4 h-4 text-white" />
                          <span className="font-mono font-bold text-white">
                            {isStudyMode ? formatTime(elapsedTime) : formatTime(timeRemaining)}
                          </span>
                          {isStudyMode && <span className="text-white/70 text-xs">elapsed</span>}
                        </div>
                        <Button
                          variant="ghost"
                          onClick={closeFreeQuiz}
                          className="text-white hover:bg-white/20"
                        >
                          ✕
                        </Button>
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div className="bg-white/20 rounded-full h-2">
                      <div 
                        className="bg-white h-2 rounded-full transition-all duration-300"
                        style={{ width: `${((currentQuestion + 1) / freeQuizData.questions.length) * 100}%` }}
                      />
                    </div>
                  </div>

                  {/* Question */}
                  <div className="p-6">
                    <div className="flex items-start gap-3 mb-6">
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center text-sm font-bold text-violet-700">
                        {currentQuestion + 1}
                      </span>
                      <div>
                        <span className="text-xs text-violet-600 uppercase tracking-wider font-semibold mb-2 block">
                          Multiple Choice
                        </span>
                        <h2 className="text-xl font-semibold leading-relaxed text-slate-900 dark:text-slate-100">
                          {freeQuizData.questions[currentQuestion].text}
                        </h2>
                      </div>
                    </div>

                    {/* Options */}
                    <div className="space-y-3">
                      {freeQuizData.questions[currentQuestion].options.map((option, index) => {
                        const isSelected = selectedAnswer === option;
                        const isCorrect = option === freeQuizData.questions[currentQuestion].correct_answer;
                        const showResult = selectedAnswer !== null;

                        return (
                          <motion.button
                            key={index}
                            whileHover={{ scale: showResult ? 1 : 1.01 }}
                            whileTap={{ scale: showResult ? 1 : 0.99 }}
                            onClick={() => handleSelectAnswer(option)}
                            disabled={selectedAnswer !== null}
                            className={`w-full text-left flex items-center gap-4 p-4 rounded-xl border-2 transition-all ${
                              showResult
                                ? isCorrect
                                  ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                                  : isSelected && !isCorrect
                                  ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                                  : 'border-slate-200 dark:border-slate-700 opacity-50'
                                : isSelected 
                                ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/20'
                                : 'border-slate-200 dark:border-slate-700 hover:border-violet-300 hover:bg-violet-50/50'
                            }`}
                          >
                            <span className={`w-9 h-9 rounded-full border-2 flex items-center justify-center text-sm font-bold transition-colors ${
                              showResult && isCorrect
                                ? 'border-green-500 bg-green-500 text-white'
                                : showResult && isSelected && !isCorrect
                                ? 'border-red-500 bg-red-500 text-white'
                                : isSelected 
                                ? 'border-violet-500 bg-violet-500 text-white' 
                                : 'border-gray-400 text-gray-600'
                            }`}>
                              {showResult && isCorrect ? (
                                <CheckCircle2 className="w-5 h-5" />
                              ) : showResult && isSelected && !isCorrect ? (
                                <XCircle className="w-5 h-5" />
                              ) : (
                                String.fromCharCode(65 + index)
                              )}
                            </span>
                            <span className="flex-1 font-medium text-base text-slate-800 dark:text-slate-200">{option}</span>
                            {!showResult && isSelected && <CheckCircle2 className="w-5 h-5 text-violet-500" />}
                          </motion.button>
                        );
                      })}
                    </div>

                    {/* Explanation after answering */}
                    {selectedAnswer && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`mt-6 rounded-xl p-5 border ${
                          selectedAnswer === freeQuizData.questions[currentQuestion].correct_answer
                            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                            : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                        }`}
                      >
                        {/* Correct/Incorrect Banner */}
                        <div className={`flex items-center gap-3 mb-4 pb-4 border-b ${
                          selectedAnswer === freeQuizData.questions[currentQuestion].correct_answer
                            ? 'border-green-200 dark:border-green-700'
                            : 'border-red-200 dark:border-red-700'
                        }`}>
                          {selectedAnswer === freeQuizData.questions[currentQuestion].correct_answer ? (
                            <>
                              <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center">
                                <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-300" />
                              </div>
                              <div>
                                <p className="font-bold text-green-700 dark:text-green-300 text-lg">Correct!</p>
                                <p className="text-sm text-green-600 dark:text-green-400">Great job! Keep up the good work.</p>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-800 flex items-center justify-center">
                                <XCircle className="w-6 h-6 text-red-600 dark:text-red-300" />
                              </div>
                              <div>
                                <p className="font-bold text-red-700 dark:text-red-300 text-lg">Incorrect</p>
                                <p className="text-sm text-red-600 dark:text-red-400">
                                  Correct answer: {freeQuizData.questions[currentQuestion].correct_answer}
                                </p>
                              </div>
                            </>
                          )}
                        </div>
                        
                        {/* Basic Explanation */}
                        <div className="flex items-start gap-2 mb-4">
                          <Lightbulb className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-semibold text-slate-900 dark:text-slate-100 mb-1">Quick Explanation</p>
                            <p className="text-sm text-slate-700 dark:text-slate-300">
                              {freeQuizData.questions[currentQuestion].explanation}
                            </p>
                          </div>
                        </div>

                        {/* AI Explanation with APA References */}
                        <div className="mt-4 p-4 bg-teal-50 dark:bg-teal-900/20 rounded-xl border border-teal-200 dark:border-teal-800">
                          <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="w-5 h-5 text-teal-600" />
                            <p className="font-bold text-teal-700 dark:text-teal-400">AI Analysis with APA References</p>
                          </div>
                          {loadingExplanation ? (
                            <div className="flex items-center gap-3">
                              <Loader2 className="w-5 h-5 text-teal-600 animate-spin" />
                              <span className="text-sm text-teal-700 dark:text-teal-400">Generating detailed explanation...</span>
                            </div>
                          ) : aiExplanation ? (
                            <StreamingText 
                              text={aiExplanation} 
                              isStreaming={loadingExplanation}
                            />
                          ) : (
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              Unable to load AI explanation. Please continue to the next question.
                            </p>
                          )}
                        </div>

                        {/* Floating Next Button - Always visible */}
                        <div className="sticky bottom-0 mt-6 pt-4 pb-2 bg-gradient-to-t from-white via-white to-transparent dark:from-slate-900 dark:via-slate-900">
                          <Button
                            onClick={handleNextQuestion}
                            className={`w-full py-6 ${
                              selectedAnswer === freeQuizData.questions[currentQuestion].correct_answer
                                ? 'bg-green-600 hover:bg-green-700'
                                : 'bg-violet-600 hover:bg-violet-700'
                            } text-white font-semibold shadow-lg`}
                            data-testid="free-quiz-next-btn"
                          >
                            {loadingExplanation ? (
                              <>
                                Skip Explanation
                                <ChevronRight className="w-5 h-5 ml-2" />
                              </>
                            ) : currentQuestion < freeQuizData.questions.length - 1 ? (
                              <>
                                Next Question
                                <ChevronRight className="w-5 h-5 ml-2" />
                              </>
                            ) : (
                              <>
                                <Trophy className="w-5 h-5 mr-2" />
                                See Results
                              </>
                            )}
                          </Button>
                        </div>
                      </motion.div>
                    )}
                  </div>
                </>
              ) : (
                /* Results Screen */
                <div className="p-8">
                  {/* Score Header */}
                  <div className="text-center mb-8">
                    <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center ${
                      getScore() >= 8 ? 'bg-green-100' : getScore() >= 6 ? 'bg-amber-100' : 'bg-red-100'
                    }`}>
                      <Trophy className={`w-10 h-10 ${
                        getScore() >= 8 ? 'text-green-600' : getScore() >= 6 ? 'text-amber-600' : 'text-red-600'
                      }`} />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">
                      Quiz Complete!
                    </h2>
                    <p className="text-4xl font-bold text-violet-600 mb-2">
                      {getScore()} / {freeQuizData.questions.length}
                    </p>
                    <p className="text-slate-600 dark:text-slate-400">
                      {getScore() >= 8 ? "Excellent work!" : getScore() >= 6 ? "Good job!" : "Keep practicing!"}
                    </p>
                  </div>

                  {/* Answer Review */}
                  <div className="mb-8">
                    <h3 className="font-bold text-lg text-slate-900 dark:text-slate-100 mb-4">Review Your Answers</h3>
                    <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                      {userAnswers.map((answer, index) => (
                        <div 
                          key={index} 
                          className={`p-4 rounded-xl border-2 ${
                            answer.isCorrect 
                              ? 'border-green-200 bg-green-50 dark:bg-green-900/20' 
                              : 'border-red-200 bg-red-50 dark:bg-red-900/20'
                          }`}
                        >
                          <div className="flex items-start gap-2 mb-2">
                            {answer.isCorrect ? (
                              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            ) : (
                              <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                            )}
                            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                              Q{index + 1}: {answer.question}
                            </p>
                          </div>
                          {!answer.isCorrect && (
                            <p className="text-sm text-red-600 ml-7 mb-1">
                              Your answer: {answer.selected || "No answer"}
                            </p>
                          )}
                          <p className="text-sm text-green-600 ml-7 mb-2">
                            Correct: {answer.correct}
                          </p>
                          
                          {/* AI Explanation with APA References */}
                          {answer.aiExplanation && (
                            <div className="ml-7 mt-3 p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg border border-teal-200 dark:border-teal-800">
                              <div className="flex items-center gap-2 mb-2">
                                <Sparkles className="w-4 h-4 text-teal-600" />
                                <p className="text-xs font-bold text-teal-700 dark:text-teal-400">AI Analysis with APA References</p>
                              </div>
                              <div className="text-xs text-slate-700 dark:text-slate-300">
                                <StreamingText text={answer.aiExplanation} isStreaming={false} />
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* CTA to Subscribe */}
                  <div className="bg-gradient-to-r from-teal-500 to-emerald-600 rounded-xl p-6 text-white text-center">
                    <Lock className="w-8 h-8 mx-auto mb-3 opacity-80" />
                    <h3 className="text-xl font-bold mb-2">Unlock Full Access</h3>
                    <p className="text-white/90 mb-4 text-sm">
                      Get access to 730+ practice questions, 4 full mock exams, AI-powered explanations, 
                      study plans, and more for just $7.00/month!
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Button
                        onClick={() => {
                          closeFreeQuiz();
                          navigate("/auth");
                        }}
                        className="bg-white text-teal-600 hover:bg-white/90 font-semibold"
                      >
                        Create Account & Subscribe
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                      <Button
                        variant="outline"
                        onClick={closeFreeQuiz}
                        className="border-white/30 text-white hover:bg-white/10"
                      >
                        Maybe Later
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sample Study Plan Modal */}
      <AnimatePresence>
        {showSampleStudyPlan && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={(e) => e.target === e.currentTarget && setShowSampleStudyPlan(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-teal-600 to-cyan-600 p-6 text-white rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-8 h-8" />
                    <div>
                      <h2 className="text-2xl font-bold">Sample Study Plan</h2>
                      <p className="text-teal-100">8-Week BCBA Exam Preparation</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowSampleStudyPlan(false)}
                    className="text-white hover:bg-white/20"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Overview Stats */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-teal-50 dark:bg-teal-900/20 p-4 rounded-xl text-center">
                    <p className="text-2xl font-bold text-teal-600">8</p>
                    <p className="text-sm text-slate-600 dark:text-slate-400">Weeks</p>
                  </div>
                  <div className="bg-violet-50 dark:bg-violet-900/20 p-4 rounded-xl text-center">
                    <p className="text-2xl font-bold text-violet-600">320+</p>
                    <p className="text-sm text-slate-600 dark:text-slate-400">Questions</p>
                  </div>
                  <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-xl text-center">
                    <p className="text-2xl font-bold text-amber-600">4</p>
                    <p className="text-sm text-slate-600 dark:text-slate-400">Mock Exams</p>
                  </div>
                </div>

                {/* Weekly Plan Preview */}
                <h3 className="font-bold text-lg text-slate-800 dark:text-slate-200 mb-4">Weekly Breakdown</h3>
                <div className="space-y-3 mb-6">
                  {[
                    { week: 1, topic: "Philosophical Foundations & Basic Concepts", tasks: ["Complete foundation quizzes", "Review key terminology", "Study core concepts"] },
                    { week: 2, topic: "Measurement & Data Collection", tasks: ["Practice measurement problems", "Master graphing concepts", "Complete IOA calculations"] },
                    { week: 3, topic: "Experimental Design", tasks: ["Learn all design types", "Practice identifying designs", "Complete design quizzes"] },
                    { week: 4, topic: "Reinforcement & Punishment", tasks: ["Master schedules of reinforcement", "Practice scenario analysis", "Mock Exam #1"] },
                    { week: 5, topic: "Stimulus Control & Verbal Behavior", tasks: ["Study Skinner's verbal operants", "Complete VB quizzes", "Dictionary review"] },
                    { week: 6, topic: "Behavior Reduction & Ethics", tasks: ["Review ethical guidelines", "Practice ethics scenarios", "Mock Exam #2"] },
                    { week: 7, topic: "FBA & Functional Analysis", tasks: ["Master FBA process", "Practice FA conditions", "Mock Exam #3"] },
                    { week: 8, topic: "Final Review & Exam Prep", tasks: ["Complete all remaining quizzes", "Final dictionary review", "Mock Exam #4"] },
                  ].map((item, index) => (
                    <div 
                      key={index}
                      className={`p-4 rounded-xl border ${index < 2 ? 'border-teal-200 bg-white dark:bg-slate-800' : 'border-slate-200 bg-slate-50 dark:bg-slate-800/50 opacity-75'}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${index < 2 ? 'bg-teal-100 text-teal-700' : 'bg-slate-200 text-slate-600'}`}>
                            {item.week}
                          </span>
                          <span className="font-semibold text-slate-800 dark:text-slate-200">{item.topic}</span>
                        </div>
                        {index >= 2 && (
                          <Lock className="w-4 h-4 text-slate-400" />
                        )}
                      </div>
                      {index < 2 && (
                        <ul className="ml-10 space-y-1">
                          {item.tasks.map((task, i) => (
                            <li key={i} className="text-sm text-slate-600 dark:text-slate-400 flex items-center gap-2">
                              <CheckCircle2 className="w-3 h-3 text-teal-500" />
                              {task}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>

                {/* Premium Features */}
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl p-5 mb-6 border border-amber-200 dark:border-amber-800">
                  <h4 className="font-bold text-amber-800 dark:text-amber-300 mb-3 flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    Premium Study Plan Features
                  </h4>
                  <ul className="space-y-2">
                    {[
                      "Personalized plan based on YOUR weak areas",
                      "Adaptive difficulty that adjusts to your progress",
                      "Daily study reminders and progress tracking",
                      "AI-powered recommendations for focus areas",
                      "Unlimited access to all quizzes and mock exams"
                    ].map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-amber-900 dark:text-amber-200">
                        <CheckCircle2 className="w-4 h-4 text-amber-600" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={() => {
                      setShowSampleStudyPlan(false);
                      navigate(isAuthenticated ? "/subscription" : "/auth");
                    }}
                    className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 h-12 text-lg font-semibold"
                  >
                    Get Your Personalized Plan - $7.00/mo
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowSampleStudyPlan(false);
                      setShowFreeQuiz(true);
                    }}
                    className="flex-1"
                  >
                    Try Free Quiz First
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
